import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  Avatar,
  Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  DateRange as DateRangeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@material-ui/icons';
import { trackPromise } from 'react-promise-tracker';
import Alert from '../../../components/Alert';
import ConsultaPlazasService from '../../../services/ConsultaPlazasService';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  searchBox: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  studentInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    marginRight: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    width: 32,
    height: 32,
    fontSize: '0.875rem',
  },
  studentDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  studentName: {
    fontWeight: 'bold',
    fontSize: '0.875rem',
  },
  studentCedula: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  servicioCell: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  dateInfo: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    fontSize: '0.75rem',
  },
  dateIcon: {
    fontSize: 14,
    marginRight: theme.spacing(0.5),
  },
  hoursChip: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    fontWeight: 'bold',
  },
  confirmadoChip: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  pendienteChip: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  statusIcon: {
    fontSize: 16,
    marginRight: theme.spacing(0.5),
  },
  summaryCard: {
    marginBottom: theme.spacing(2),
    backgroundColor: '#f5f5f5',
  },
}));

const DetallePlazas = ({ carreraId, universidadId }) => {
  const classes = useStyles();
  
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (carreraId && universidadId) {
      loadData();
    }
  }, [carreraId, universidadId]);

  useEffect(() => {
    filterData();
  }, [data, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await trackPromise(
        ConsultaPlazasService.getDetallePlazas(carreraId, universidadId)
      );
      setData(result || []);
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      Alert.showServerError(error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    if (!searchTerm) {
      setFilteredData(data);
      return;
    }

    const filtered = data.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (item.servicio && item.servicio.toLowerCase().includes(searchLower)) ||
        (item.nombre_estudiante && item.nombre_estudiante.toLowerCase().includes(searchLower)) ||
        (item.apellido_estudiante && item.apellido_estudiante.toLowerCase().includes(searchLower)) ||
        (item.cedula_estudiante && item.cedula_estudiante.includes(searchTerm)) ||
        (item.region && item.region.toLowerCase().includes(searchLower))
      );
    });
    
    setFilteredData(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const getInitials = (nombre, apellido) => {
    return `${nombre?.charAt(0) || ''}${apellido?.charAt(0) || ''}`.toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('es-PY');
    } catch {
      return 'N/A';
    }
  };

  const renderSummaryCard = () => {
    const totalEstudiantes = data.length;
    const confirmadosInstitucion = data.filter(item => 
      item.estado_institucion?.includes('Confirmado')).length;
    const confirmadosEstablecimiento = data.filter(item => 
      item.estado_establecimiento?.includes('Confirmado')).length;
    const totalHoras = data.reduce((sum, item) => sum + (item.total_horas || 0), 0);

    return (
      <Card className={classes.summaryCard}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Resumen del Detalle
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary">
                Total Estudiantes
              </Typography>
              <Typography variant="h6" color="primary">
                {totalEstudiantes}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary">
                Confirmados por Institución
              </Typography>
              <Typography variant="h6" style={{ color: '#4caf50' }}>
                {confirmadosInstitucion} ({Math.round((confirmadosInstitucion/totalEstudiantes)*100)}%)
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary">
                Confirmados por Establecimiento
              </Typography>
              <Typography variant="h6" style={{ color: '#4caf50' }}>
                {confirmadosEstablecimiento} ({Math.round((confirmadosEstablecimiento/totalEstudiantes)*100)}%)
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary">
                Total Horas de Práctica
              </Typography>
              <Typography variant="h6" color="primary">
                {totalHoras.toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderStudentInfo = (item) => (
    <div className={classes.studentInfo}>
      <Avatar className={classes.avatar}>
        {getInitials(item.nombre_estudiante, item.apellido_estudiante)}
      </Avatar>
      <div className={classes.studentDetails}>
        <Typography className={classes.studentName}>
          {`${item.nombre_estudiante} ${item.apellido_estudiante}`}
        </Typography>
        <Typography className={classes.studentCedula}>
          CI: {item.cedula_estudiante}
        </Typography>
      </div>
    </div>
  );

  const renderDateInfo = (item) => (
    <Box>
      <div className={classes.dateInfo}>
        <DateRangeIcon className={classes.dateIcon} />
        <span>Desde: {formatDate(item.fecha_desde)}</span>
      </div>
      <div className={classes.dateInfo}>
        <DateRangeIcon className={classes.dateIcon} />
        <span>Hasta: {formatDate(item.fecha_hasta)}</span>
      </div>
      <div className={classes.dateInfo}>
        <ScheduleIcon className={classes.dateIcon} />
        <span>Año: {item.año_practica}</span>
      </div>
    </Box>
  );

  const renderStatusChip = (status, isConfirmed) => (
    <Chip
      icon={isConfirmed ? <CheckCircleIcon className={classes.statusIcon} /> : <WarningIcon className={classes.statusIcon} />}
      label={status}
      size="small"
      className={isConfirmed ? classes.confirmadoChip : classes.pendienteChip}
    />
  );

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  if (!carreraId || !universidadId) {
    return (
      <Paper style={{ padding: 20, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          Selecciona una carrera y universidad para ver el detalle
        </Typography>
      </Paper>
    );
  }

  return (
    <div className={classes.root}>
      {data.length > 0 && renderSummaryCard()}
      
      <Paper className={classes.paper}>
        <Box className={classes.searchBox}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar por servicio, estudiante, cédula o región..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Estudiante</TableCell>
                <TableCell>Servicio / Región</TableCell>
                <TableCell>Fechas y Duración</TableCell>
                <TableCell>Horas</TableCell>
                <TableCell>Estado Institución</TableCell>
                <TableCell>Estado Establecimiento</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {renderStudentInfo(item)}
                    </TableCell>
                    
                    <TableCell>
                      <Typography className={classes.servicioCell}>
                        {item.servicio}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {item.region || 'Sin región'} - {item.nivel_servicio || 'Sin nivel'}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      {renderDateInfo(item)}
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        label={`${item.total_horas || 0} hrs`}
                        className={classes.hoursChip}
                        size="small"
                      />
                    </TableCell>
                    
                    <TableCell>
                      {renderStatusChip(
                        item.estado_institucion,
                        item.estado_institucion?.includes('Confirmado')
                      )}
                    </TableCell>
                    
                    <TableCell>
                      {renderStatusChip(
                        item.estado_establecimiento,
                        item.estado_establecimiento?.includes('Confirmado')
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>
      
      {filteredData.length === 0 && !loading && (
        <Paper style={{ padding: 20, textAlign: 'center', marginTop: 20 }}>
          <Typography variant="h6" color="textSecondary">
            No se encontraron detalles de plazas
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {searchTerm ? 'Intenta con otros términos de búsqueda' : 'No hay datos disponibles para esta combinación'}
          </Typography>
        </Paper>
      )}
    </div>
  );
};

export default DetallePlazas;
